import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Alert,
  Snackbar,
  alpha,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ml from "./images/MercadoLibre.svg";
import tn from "./images/TiendaNube.svg";
import mgto from "./images/Magento.svg";
import shopy from "./images/Shopify.svg";
import falabella from "./images/Falabella.svg";
import vtex from "./images/Vtex.svg";
import amazon from "./images/Amazon.svg";
import paris from "./images/Cencosud.svg";
import fravega from "./images/Fravega.svg";
import product from "./images/Product.svg";
import andreani from "./images/Andreani.svg";
import Correo from "./images/Correo.svg";
import Oca from "./images/Oca.svg";
import Urbano from "./images/Urbano.svg";
import Enviopack from "./images/EnvioPack.svg";
import Pickit from "./images/Pickit.svg";
import Moova from "./images/Moova.svg";
import Flow from "./images/Flow.svg";
import Zipnova from "./images/Zipnova.svg";
import Route from "./images/Route.svg";
import WooCommerce from "./images/WooCommerce.svg";
import Epsa from "./images/Epsa.svg";
import MercadoLibreModal from "./components/mercadolibremodal";
import TiendaNubeModal from "./components/tiendanubemodal";
import WooCommerceModal from "./components/woocommercemodal";
import LogisticsModal from "./components/logisticsmodal";
import integrationService from "../../services/integrationService";

const integrations = [
  { id: 1, name: "Mercado Libre", logoUrl: ml, comingSoon: false, partner: "mercadolibre" },
  { id: 2, name: "TiendaNube", logoUrl: tn, comingSoon: false, partner: "tiendanube" },
  { id: 3, name: "WooCommerce", logoUrl: WooCommerce, comingSoon: false, partner: "woocommerce" },
  //{ id: 4, name: "Magento", logoUrl: mgto, comingSoon: true },
  //{ id: 5, name: "VTEX", logoUrl: vtex, comingSoon: true },
  //{ id: 6, name: "Amazon", logoUrl: amazon, comingSoon: true },
  //{ id: 7, name: "Paris Cencosud", logoUrl: paris, comingSoon: true },
  //{ id: 8, name: "PrestaShop", logoUrl: falabella, comingSoon: true },
  //{ id: 9, name: "Fravega", logoUrl: fravega, comingSoon: true },
  //{ id: 10, name: "Product", logoUrl: product, comingSoon: true },
];

const logistics = [
  { id: 1, name: "Andreani", logoUrl: andreani, comingSoon: false, partner: "andreani" },
  { id: 2, name: "Urbano", logoUrl: Urbano, comingSoon: false, partner: "urbano" },
  { id: 3, name: "Oca", logoUrl: Oca, comingSoon: true },
  //{ id: 4, name: "Correo", logoUrl: Correo, comingSoon: true },
  //{ id: 5, name: "Pickit", logoUrl: Pickit, comingSoon: true },
  //{ id: 6, name: "Moova", logoUrl: Moova, comingSoon: true },
  //{ id: 7, name: "Zipnova", logoUrl: Zipnova, comingSoon: true },
  { id: 8, name: "Enviopack", logoUrl: Enviopack, comingSoon: false, partner: "enviopack" },
  //{ id: 9, name: "Route", logoUrl: Route, comingSoon: true },
  //{ id: 10, name: "Flow", logoUrl: Flow, comingSoon: true },
  { id: 11, name: "Epsa", logoUrl: Epsa, comingSoon: false, partner: "epsa" },
];

// Paqueterías con carga de credenciales habilitada (mismo llenado que marketplaces).
const configurableLogistics = ["Andreani", "Urbano", "Enviopack", "Epsa"];

const IntegrationCard = ({ integration, onClick, connectionCount = 0 }) => {
  const isConfigurable = [
    "Mercado Libre",
    "TiendaNube",
    "WooCommerce",
    ...configurableLogistics,
  ].includes(integration.name);
  const connected = !integration.comingSoon && connectionCount > 0;

  return (
    <Card
      onClick={() => isConfigurable && onClick(integration)}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        borderRadius: 3,
        border: "1px solid",
        borderColor: integration.comingSoon ? "grey.200" : "primary.main",
        boxShadow: "none",
        overflow: "hidden",
        opacity: integration.comingSoon ? 0.7 : 1,
        cursor: isConfigurable ? "pointer" : "default",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: isConfigurable ? "0 4px 12px rgba(0,0,0,0.08)" : "none",
          transform: isConfigurable ? "translateY(-2px)" : "none",
        },
      }}
    >
      <CardContent
        sx={{
          textAlign: "center",
          py: 3,
          px: 2,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          component="img"
          src={integration.logoUrl}
          alt={integration.name}
          sx={{
            width: 80,
            height: 80,
            objectFit: "contain",
            filter: integration.comingSoon ? "grayscale(40%)" : "none",
          }}
        />
        <Typography
          variant="body2"
          fontWeight={600}
          color={integration.comingSoon ? "text.secondary" : "text.primary"}
        >
          {integration.name}
        </Typography>
        <Chip
          icon={
            integration.comingSoon ? (
              <ScheduleIcon sx={{ fontSize: "14px !important" }} />
            ) : connected ? (
              <CheckCircleIcon sx={{ fontSize: "14px !important" }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />
            )
          }
          label={
            integration.comingSoon
              ? "Proximamente"
              : connected
              ? `Conectado (${connectionCount})`
              : "Disponible"
          }
          size="small"
          sx={{
            fontSize: "0.7rem",
            fontWeight: 500,
            height: 24,
            bgcolor: integration.comingSoon
              ? "grey.100"
              : connected
              ? (theme) => alpha(theme.palette.primary.main, 0.12)
              : (theme) => alpha(theme.palette.success.main, 0.1),
            color: integration.comingSoon
              ? "text.secondary"
              : connected
              ? "primary.dark"
              : "success.dark",
            border: "none",
          }}
        />
      </CardContent>
    </Card>
  );
};

const SectionHeader = ({ title, count, availableCount }) => (
  <Box sx={{ mb: 2.5, mt: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Typography variant="h6" fontWeight={600} color="text.primary">
        {title}
      </Typography>
      <Chip
        label={`${availableCount}/${count} disponibles`}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 500, fontSize: "0.72rem" }}
      />
    </Box>
  </Box>
);

const Integrations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mlModalOpen, setMlModalOpen] = useState(false);
  const [tnModalOpen, setTnModalOpen] = useState(false);
  const [wooModalOpen, setWooModalOpen] = useState(false);
  // Paquetería seleccionada para cargar credenciales (o null si el modal está cerrado).
  const [selectedLogistics, setSelectedLogistics] = useState(null);
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "error" });
  const [integrationsData, setIntegrationsData] = useState([]);

  const fetchIntegrations = useCallback(async () => {
    try {
      const data = await integrationService.getIntegrations();
      setIntegrationsData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error al cargar integraciones:", error);
    }
  }, []);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const connectionsByPartner = (partner) =>
    integrationsData.filter((item) => item.integration_partner === partner);

  const handleDeleteConnection = async (id) => {
    try {
      await integrationService.deleteIntegration(id);
      await fetchIntegrations();
      setFeedback({ open: true, message: "Conexión eliminada.", severity: "success" });
    } catch (error) {
      setFeedback({ open: true, message: "No se pudo eliminar la conexión.", severity: "error" });
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const isConnected = query.get("ml_connected");
    const mlError = query.get("ml_error");
    const tnConnected = query.get("tn_connected");
    const tnError = query.get("tn_error");

    if (isConnected === "true") {
      setFeedback({
        open: true,
        message: "Cuenta de MercadoLibre conectada exitosamente.",
        severity: "success",
      });
      fetchIntegrations();
      navigate(location.pathname, { replace: true });
      return;
    }

    if (mlError) {
      setFeedback({
        open: true,
        message: `No se pudo conectar MercadoLibre: ${decodeURIComponent(mlError)}`,
        severity: "error",
      });
      navigate(location.pathname, { replace: true });
      return;
    }

    if (tnConnected === "true") {
      setFeedback({
        open: true,
        message: "Cuenta de Tienda Nube conectada exitosamente.",
        severity: "success",
      });
      fetchIntegrations();
      navigate(location.pathname, { replace: true });
      return;
    }

    if (tnError) {
      setFeedback({
        open: true,
        message: `No se pudo conectar Tienda Nube: ${decodeURIComponent(tnError)}`,
        severity: "error",
      });
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate, fetchIntegrations]);

  const marketplaceAvailable = integrations.filter((i) => !i.comingSoon).length;
  const logisticsAvailable = logistics.filter((i) => !i.comingSoon).length;

  const handleSaveMercadoLibreCredentials = async ({ clientId, clientSecret }) => {
    const response = await integrationService.saveMercadoLibreCredentials({
      clientId,
      clientSecret,
    });

    const authUrl = response?.auth_url;
    if (!authUrl) {
      throw new Error("No se recibió URL de autorización desde el backend.");
    }

    window.location.assign(authUrl);
  };

  const handleSaveTiendaNubeCredentials = async ({ clientId, clientSecret }) => {
    const response = await integrationService.saveTiendanubeCredentials({
      clientId,
      clientSecret,
    });

    const authUrl = response?.auth_url;
    if (!authUrl) {
      throw new Error("No se recibió URL de autorización desde el backend.");
    }

    window.location.assign(authUrl);
  };

  const handleSaveWooCommerceCredentials = async ({ storeUrl, consumerKey, consumerSecret }) => {
    await integrationService.saveWooCommerceCredentials({ storeUrl, consumerKey, consumerSecret });
    await fetchIntegrations();
    setFeedback({
      open: true,
      message: "Tienda WooCommerce conectada exitosamente.",
      severity: "success",
    });
  };

  const handleSaveLogisticsCredentials = async ({ clientId, clientSecret }) => {
    if (!selectedLogistics?.partner) return;
    await integrationService.saveLogisticsCredentials(selectedLogistics.partner, {
      clientId,
      clientSecret,
    });
    await fetchIntegrations();
    setFeedback({
      open: true,
      message: `${selectedLogistics.name} conectado exitosamente.`,
      severity: "success",
    });
  };

  const handleIntegrationClick = (integration) => {
    if (integration.name === "Mercado Libre") {
      setMlModalOpen(true);
      return;
    }
    if (integration.name === "TiendaNube") {
      setTnModalOpen(true);
      return;
    }
    if (integration.name === "WooCommerce") {
      setWooModalOpen(true);
      return;
    }
    // Paqueterías configurables: abrimos el modal genérico con el carrier elegido.
    if (integration.partner) {
      setSelectedLogistics(integration);
    }
  };

  return (
    <Box sx={{ px: 5, py: 3 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: "#1a1a1a",
          pl: 1.5,
          mb: 3,
          borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
          lineHeight: 1.5,
        }}
      >
        Integraciones
      </Typography>

      <SectionHeader
        title="Marketplaces y Shops"
        count={integrations.length}
        availableCount={marketplaceAvailable}
      />
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {integrations.map((integration) => (
          <Grid item xs={6} sm={4} md={3} lg={2.4} key={integration.id}>
            <IntegrationCard
              integration={integration}
              onClick={handleIntegrationClick}
              connectionCount={
                connectionsByPartner(integration.partner).filter((c) => c.connected).length
              }
            />
          </Grid>
        ))}
      </Grid>

      <SectionHeader
        title="Logistica"
        count={logistics.length}
        availableCount={logisticsAvailable}
      />
      <Grid container spacing={2}>
        {logistics.map((integration) => (
          <Grid item xs={6} sm={4} md={3} lg={2.4} key={integration.id}>
            <IntegrationCard
              integration={integration}
              onClick={handleIntegrationClick}
              connectionCount={
                integration.partner
                  ? connectionsByPartner(integration.partner).filter((c) => c.connected).length
                  : 0
              }
            />
          </Grid>
        ))}
      </Grid>

      <MercadoLibreModal
        open={mlModalOpen}
        onClose={() => setMlModalOpen(false)}
        onSave={handleSaveMercadoLibreCredentials}
        connections={connectionsByPartner("mercadolibre")}
        onDelete={handleDeleteConnection}
      />
      <TiendaNubeModal
        open={tnModalOpen}
        onClose={() => setTnModalOpen(false)}
        onSave={handleSaveTiendaNubeCredentials}
        connections={connectionsByPartner("tiendanube")}
        onDelete={handleDeleteConnection}
      />
      <WooCommerceModal
        open={wooModalOpen}
        onClose={() => setWooModalOpen(false)}
        onSave={handleSaveWooCommerceCredentials}
        connections={connectionsByPartner("woocommerce")}
        onDelete={handleDeleteConnection}
      />
      <LogisticsModal
        open={Boolean(selectedLogistics)}
        onClose={() => setSelectedLogistics(null)}
        onSave={handleSaveLogisticsCredentials}
        carrier={selectedLogistics}
        connections={
          selectedLogistics ? connectionsByPartner(selectedLogistics.partner) : []
        }
        onDelete={handleDeleteConnection}
      />
      <Snackbar
        open={feedback.open}
        autoHideDuration={5000}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
          severity={feedback.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Integrations;